// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useLocation, useNavigate } from 'react-router';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { useAppSelector } from '@advisor-app/store/store';
import { useValidationPortfolioLoaded } from './use-validate-portfolio-loaded';
import { useValidateSoldPosition } from './use-validate-sold-position';
import {
  MODAL_TEMPLATES_OPTIONS,
  showModal,
} from '@shared/ui-kit/components/popups/show-modal';
import { selectWorkflowType } from '@advisor-app/store/slices/workflow';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export const useLayoutValidations = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const workflow = useAppSelector(selectWorkflowType);
  const { t } = useTranslation();

  const { isPortfolioLoaded, requiresPortfolioLoaded } =
    useValidationPortfolioLoaded();

  const { pagesThatNeedSoldPosition, isSomeSellPosition, is10PercentSold } =
    useValidateSoldPosition();

  useEffect(() => {
    const workflowWithSoldValidation = ['load', 'rebalance'];

    const renderPopup = (text: string) => {
      showModal({
        ...MODAL_TEMPLATES_OPTIONS(t).UNHANDLED_ERROR,
        title: t('common.oops', { ns: 'common' }) as string,
        content: text,
      });
    };

    if (!isPortfolioLoaded && requiresPortfolioLoaded(pathname)) {
      renderPopup(
        t('common.upload-portfolio-required', { ns: 'common' }) as string,
      );

      navigate('/');
    }

    if (
      workflowWithSoldValidation.includes(workflow) &&
      pagesThatNeedSoldPosition.includes(pathname as Paths)
    ) {
      if (!is10PercentSold) {
        renderPopup(
          t('common.sell-10-percent-required', { ns: 'common' }) as string,
        );
        navigate('/tax-manager');
      }

      if (!isSomeSellPosition) {
        renderPopup(t('common.sell-position-required', { ns: 'common' }) as string);

        navigate('/tax-manager');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, t]);
};
